import {z} from 'zod';
export type {JsonPrimitive, JsonObject, JsonValue} from 'type-fest';
import type {JsonObject, JsonValue} from 'type-fest';

export type SharedLLMMessage =
  | {
      role: 'system';
      content: string;
    }
  | {
      role: 'user';
      content:
        | string
        | Array<
            | {type: 'text'; text: string}
            | {
                type: 'image_url';
                imageUrl: string;
                detail?: 'auto' | 'low' | 'high';
              }
          >;
    }
  | {
      role: 'assistant';
      content?: string | null;
      toolCalls?: Array<{
        id: string;
        name: string;
        input: JsonValue;
      }>;
    }
  | {
      role: 'tool';
      toolCallId: string;
      content:
        | string
        | Array<
            {type: 'text'; text: string} | {type: 'image_url'; imageUrl: string}
          >;
    };

export const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(JsonValueSchema),
    z.record(JsonValueSchema),
  ])
);
export const JsonObjectSchema: z.ZodType<JsonObject> =
  z.record(JsonValueSchema);

export type TextEventMetadata = JsonObject & {
  streaming?: boolean;
  final?: boolean;
  done?: boolean;
  error?: {message?: string};
  feedback?: {score: number};
  spanId?: string;
};

export type ExternalToolEventMetadata = JsonObject & {
  timeout?: number;
  retryCount?: number;
};

export type PlanningEventMetadata = JsonObject & {
  confidence?: number;
};

export interface EventExecutionContext {
  nodeId?: string;
  turnId?: string;
  parentEventId?: string;
  duration?: number;
  continuationEntryId?: string;
  continuationAttemptId?: string;
}

export interface EventRelationships {
  parentId?: string;
  correlationId?: string;
  causedBy?: 'user_input' | 'tool_result' | 'external_response' | 'system';
}

export interface EventObservability {
  traceId?: string;
  tags?: Record<string, string>;
}

export interface ConversationContext {
  turnNumber: number;
  messageCount: number;
  tokenCount?: number;
  topicId?: string;
}

export interface FrameworkError {
  message: string;
  code?: string;
  stack?: string;
  cause?: string;
}

export interface FrameworkEvent {
  id: string;
  type: string;
  timestamp: Date;
  sessionId: string;
  source: 'live' | 'executed';
  sequenceNumber: number;
  spanId?: string;
  execution?: EventExecutionContext;
  relationships?: EventRelationships;
  observability?: EventObservability;
  conversationContext?: ConversationContext;
  extensions?: JsonObject;
}

export interface ImageReference {
  url: string;
  detail?: 'auto' | 'low' | 'high';
  originalFileName?: string;
  /** Private key of the unmodified file behind `url`; see `AgentServiceImage`. */
  originalRef?: string;
}

export interface UserInputEvent extends FrameworkEvent {
  type: 'user_input';
  content: string;
  turnNumber?: number;
  context?: JsonObject;
  images?: ImageReference[];
  metadata?: JsonObject;
}

export interface TextEvent extends FrameworkEvent {
  type: 'text';
  content: string;
  role?: 'user' | 'assistant' | 'system';
  category?: 'streaming' | 'status' | 'final' | 'debug';
  metadata?: TextEventMetadata;
}

export interface ReasoningEvent extends FrameworkEvent {
  type: 'reasoning';
  delta: string;
  status?: 'streaming' | 'final';
}

export interface RefusalEvent extends FrameworkEvent {
  type: 'refusal';
  message: string;
  reason?: 'undeclared_tool_call' | 'safety' | 'other';
}

export interface ToolDefinitionInfo {
  name: string;
  category?: 'internal' | 'external' | 'system';
}

export type ToolExecutionStatus =
  | 'executing'
  | 'executed'
  | 'partial'
  | 'failed'
  | 'cancelled';

export type ToolEventKind = 'internal' | 'external';

export interface ToolStatusEvent extends FrameworkEvent {
  type: 'tool_status';
  status: ToolExecutionStatus;
  tool: string;
  toolCallGroupId?: string;
  input?: JsonValue;
  content?: JsonValue;
  isExternal?: boolean;
  /**
   * Optional so the type system forces readers through {@link
   * resolveToolEventKind}: events persisted before this field existed
   * still need to be readable, and the resolver falls back to the
   * legacy `isExternal` boolean.
   */
  kind?: ToolEventKind;
  callId: string;
  startTime?: Date;
  endTime?: Date;
  error?: FrameworkError;
  count?: number;
}

/**
 * Resolve the kind of a `tool_status` event, falling back to the legacy
 * `isExternal` boolean for events written before `kind` existed.
 */
export function resolveToolEventKind(
  event: Pick<ToolStatusEvent, 'kind' | 'isExternal'>
): ToolEventKind {
  return event.kind ?? (event.isExternal ? 'external' : 'internal');
}

export interface ToolPartialEvent extends FrameworkEvent {
  type: 'tool_partial';
  tool: string;
  toolCallId?: string;
  args?: JsonValue;
  isUpdate: boolean;
  isComplete: boolean;
}

export interface ExternalToolRequest {
  args: JsonObject;
  timeout?: number;
  retryPolicy?: {
    maxRetries: number;
    backoffMs: number;
  };
}

export interface ExternalToolResponse {
  result?: JsonValue;
  error?: FrameworkError;
}

export interface ExternalToolEvent extends FrameworkEvent {
  type: 'external_tool_call' | 'external_tool_response';
  callId: string;
  toolCallGroupId?: string;
  tool?: ToolDefinitionInfo;
  request?: ExternalToolRequest;
  response?: ExternalToolResponse;
  status: ToolExecutionStatus;
  metadata?: ExternalToolEventMetadata;
}

export interface ToolApprovalRequiredEvent extends FrameworkEvent {
  type: 'tool_approval_required';
  callId: string;
  toolCallGroupId?: string;
  tool: ToolDefinitionInfo;
  input?: JsonValue;
  approvalId: string;
  reason?: string;
}

export interface ToolApprovalResolvedEvent extends FrameworkEvent {
  type: 'tool_approval_resolved';
  callId: string;
  toolCallGroupId?: string;
  approvalId: string;
  decision: 'approved' | 'rejected';
  rejectionReason?: string;
}

export type ToolResultAckEventType =
  | 'tool_result_accepted'
  | 'tool_result_duplicate_accepted'
  | 'tool_result_ignored'
  | 'tool_result_conflict';

export interface ToolResultAckEvent extends FrameworkEvent {
  type: ToolResultAckEventType;
  callId: string;
  toolCallGroupId: string;
  responseKey?: string;
  reason?: string;
}

export type ToolApprovalDecisionAckEventType =
  | 'tool_approval_decision_accepted'
  | 'tool_approval_decision_duplicate_accepted'
  | 'tool_approval_decision_ignored'
  | 'tool_approval_decision_conflict';

export interface ToolApprovalDecisionAckEvent extends FrameworkEvent {
  type: ToolApprovalDecisionAckEventType;
  callId: string;
  toolCallGroupId: string;
  approvalId: string;
  reason?: string;
}

export type ErrorReason =
  | 'context_limit'
  /**
   * The agent graph hit its recursion/step limit (LangGraph
   * `GRAPH_RECURSION_LIMIT`) without reaching a stop condition. Surfaced with a
   * clean message so consumers never see the raw framework string or its
   * troubleshooting URL.
   */
  | 'step_limit_exceeded'
  | 'truncated_tool_call'
  /**
   * The graph received a tool_result callback for a tool_call id that has no
   * matching pending tool_call in the assistant's message history. Surfaced
   * when the FE retries a result the server has already accepted-and-advanced
   * past, or when the FE crashes mid-turn and replays from stale state.
   * `metadata.toolCallId` carries the offending id so callers can reconcile
   * without regex-parsing error strings.
   */
  | 'no_previous_tool_call'
  /**
   * The LLM provider rejected the request because a tool_call id the
   * framework sent it had no corresponding result in the conversation
   * history (e.g. OpenAI: `tool_call_id 'call_X' not found in tool_calls
   * of previous message`). `metadata.toolCallId` carries the offending id.
   */
  | 'tool_call_id_not_found'
  /**
   * The session was erased while a client was still streaming it, so no
   * worker can post the turn's terminal event. The stream endpoint sends this
   * itself and then ends the stream (EXT-2610).
   */
  | 'session_erased'
  /**
   * A Slack turn with inline thread history was delivered again after it had
   * already started, so the worker ended it instead of running it twice: with
   * no stored checkpoint, a re-run can't see the first attempt's tool calls
   * (EXT-2243 W3).
   */
  | 'turn_interrupted';

export interface ErrorEvent extends FrameworkEvent {
  type: 'error';
  error: FrameworkError;
  context: {
    node?: string;
    operation?: string;
  };
  recoverable: boolean;
  reason?: ErrorReason;
  /**
   * Reason-specific structured payload. For `no_previous_tool_call` and
   * `tool_call_id_not_found`, includes `{toolCallId: string}` so consumers
   * can recover without parsing `error.message`.
   */
  metadata?: JsonObject;
}

export interface FinalEvent extends FrameworkEvent {
  type: 'final';
  result: JsonValue | null;
  conversation: {
    messageHistory: SharedLLMMessage[];
    toolCalls: ToolStatusEvent[];
    externalToolCalls: ExternalToolEvent[];
    userInput: unknown;
  };
  session: {
    sessionId: string;
    startTime: Date;
    endTime: Date;
    nodeHistory: string[];
    executionDuration: number;
  };

  context: JsonObject;
}

/**
 * Reasons the framework yielded the graph back to the orchestrator. Each
 * reason represents a specific external dependency that must resolve
 * before the graph can re-enter the model.
 */
export type YieldReason =
  /** Awaiting tool_result callbacks for external (browser-executed) tools. */
  | 'awaiting_external_callback'
  /** Awaiting a server-approval decision before executing an internal tool. */
  | 'awaiting_approval';

/**
 * Emitted when `handleToolNode` ends the current graph entry not because
 * the conversation finished, but because it dispatched external tools (or
 * raised a server-approval prompt) and is now waiting for the orchestrator
 * to call back with results / decisions. Distinct from `final` so the FE
 * can keep a "still in progress" state alive across the pause instead of
 * marking the conversation done. The next `final` will arrive after the
 * callback comes in and the model produces its response.
 */
export interface YieldEvent extends FrameworkEvent {
  type: 'yield';
  reason: YieldReason;
  /**
   * The tool-call group that triggered the yield. Useful for the FE to
   * correlate the yield with the in-flight tool calls it's expected to
   * resolve.
   */
  toolCallGroupId: string;
  /** Call IDs the orchestrator is waiting on. */
  pendingCallIds: string[];
}

export interface DebugEvent extends FrameworkEvent {
  type: 'debug';
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  context?: string;
  data?: JsonValue;
  metadata?: JsonObject;
}

export interface ContextUpdateEvent extends FrameworkEvent {
  type: 'context_update';
  contextType: 'memory' | 'preference' | 'state' | 'metadata';
  update: JsonObject;
  metadata?: JsonObject;
}

export interface PromptStatusEvent extends FrameworkEvent {
  type: 'prompt_status';
  status: 'manipulated' | 'generated' | 'enhanced';
  basePrompt?: string;
  finalPrompt?: string;
  promptType?: 'system' | 'user';
  context?: JsonObject & {
    toolResults?: JsonValue[];
  };
}

export interface PhaseEvent extends FrameworkEvent {
  type: 'phase';
  phase: 'response_generated' | 'tool_routing_completed' | 'tool_completed';
  node: string;
  toolName?: string;
  duration?: number;
  metadata?: JsonObject;
}

export interface PlanningEvent extends FrameworkEvent {
  type: 'planning';
  plan: string;
  planType?: 'initial' | 'updated' | 'revised';
  metadata?: PlanningEventMetadata;
}

export interface ToolEvent extends FrameworkEvent {
  type: 'tool';

  tool: string | {name: string; category?: string};
  status: ToolExecutionStatus;
  input?: JsonValue;
  content?: JsonValue;
  callId: string;
  startTime?: Date;
  endTime?: Date;
  metadata?: JsonObject;
}

export type AnyFrameworkEvent =
  | UserInputEvent
  | TextEvent
  | ReasoningEvent
  | RefusalEvent
  | ToolStatusEvent
  | ToolPartialEvent
  | ExternalToolEvent
  | ToolApprovalRequiredEvent
  | ToolApprovalResolvedEvent
  | ToolResultAckEvent
  | ToolApprovalDecisionAckEvent
  | ErrorEvent
  | FinalEvent
  | YieldEvent
  | DebugEvent
  | ContextUpdateEvent
  | PromptStatusEvent
  | PhaseEvent
  | PlanningEvent
  | ToolEvent;
